import React, { useEffect, useState } from "react";
import { fetchFromStrapi } from "../api/strapi";
import { Link } from "react-router-dom";
import ServicesSkeletonLoader from "./ServicesSkeletonLoader";
import { EmptyState } from "./SkeletonUI";
import DOMPurify from 'dompurify';
import LazyImage from "./LazyImage";

function ServiceCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const imageUrl = item.attributes?.Image?.data?.attributes?.url;
  const sanitizedHtmlDescription = DOMPurify.sanitize(item.attributes?.Description || "");

  return (
    <Link to={`/services/${item.id}`} style={{ textDecoration: "none", color: "inherit", display: "block", height: "100%" }}>
      <article
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          overflow: "hidden",
          background: "#fff",
          transition: "box-shadow 0.22s ease, transform 0.22s ease",
          boxShadow: hovered ? "0 12px 32px rgba(0,0,0,0.10)" : "0 2px 8px rgba(0,0,0,0.05)",
          transform: hovered ? "translateY(-4px)" : "translateY(0)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Image */}
        <div style={{ height: 220, overflow: "hidden", flexShrink: 0 }}>
          <LazyImage
            src={imageUrl}
            alt={item.attributes?.Title}
            objectFit="cover"
            style={{
              transition: "transform 0.4s ease",
              transform: hovered ? "scale(1.04)" : "scale(1)",
            }}
          />
        </div>

        {/* Content */}
        <div style={{ padding: "22px 24px 28px", display: "flex", flexDirection: "column", flex: 1 }}>
          <h3 style={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: "#0a1a3a",
            marginBottom: 10,
            lineHeight: 1.35,
          }}>
            {item.attributes?.Title}
          </h3>

          {item.attributes?.Description && (
            <p style={{
              color: "#6B7280",
              fontSize: "0.875rem",
              lineHeight: 1.65,
              marginBottom: 20,
              flex: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {<div dangerouslySetInnerHTML={{ __html: sanitizedHtmlDescription }} />}
            </p>
          )}

          <span style={{ color: "#009EDC", fontSize: "0.875rem", fontWeight: 600 }}>
            Learn more 
          </span>
        </div>
      </article>
    </Link>
  );
}

const ServiceCarousel = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetchFromStrapi("/ecl-services-sections");
        setServices(res?.data?.data || res?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) return <ServicesSkeletonLoader />;

  return (
    <section className="container" style={{ padding: "72px 0 80px" }}>
      <p className="text-uppercase fw-semibold mb-2"
        style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
        What we offer
      </p>
      <h2 className="fw-bold mb-5"
        style={{ fontSize: "clamp(1.75rem, 2.5vw, 2.25rem)", color: "#0a1a3a" }}>
        Our Solutions
      </h2>

      {services.length === 0 ? (
        <EmptyState
          title="No solutions yet"
          description="We're adding our solutions here. Check back soon."
        />
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 24,
        }}>
          {services.map(item => (
            <ServiceCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
};

export default ServiceCarousel;
